import {
    AP_ID,
    MP_ID,
    VITALITY_ID,
    STRENGTH_ID,
    INTELLIGENCE_ID,
    CHANCE_ID,
    AGILITY_ID,
    POWER_ID,
    DAMAGE_ID,
    CRIT_ID,
    CRIT_DAMAGE_ID,
    SPELL_PER_DMG_ID,
    FINAL_DAMAGE_ID,
} from '../core/st';
import { calcSpellRows, getTypeId, isEliotropeClass, isPercentMaxHpHealLine } from '../core/spellUtils';

const PUSH_DMG_ID = 84;
const HEAL_STAT_ID = 49;
const WEAPON_PER_DMG_ID = 122;

const ELM_STAT_ID = {
    fire: INTELLIGENCE_ID,
    air: AGILITY_ID,
    water: CHANCE_ID,
    earth: STRENGTH_ID,
    neutral: STRENGTH_ID,
};

const ELM_DMG_ID = {
    fire: 89,
    air: 91,
    water: 90,
    earth: 88,
    neutral: 92,
};

const BEST_ELEM_PAIRS = [
    [STRENGTH_ID, ELM_DMG_ID.earth],
    [STRENGTH_ID, ELM_DMG_ID.neutral],
    [INTELLIGENCE_ID, ELM_DMG_ID.fire],
    [CHANCE_ID, ELM_DMG_ID.water],
    [AGILITY_ID, ELM_DMG_ID.air],
];

const BEST_HEAL_PRIMARY_IDS = [STRENGTH_ID, INTELLIGENCE_ID, CHANCE_ID, AGILITY_ID];

const ELEMENTAL_STAT_IDS = new Set([STRENGTH_ID, INTELLIGENCE_ID, CHANCE_ID, AGILITY_ID]);

/** Matches backend DEFENSE_PRECOMPUTE_STAT_IDS in sa_scoring.py */
export const DEFENSE_CARD_STAT_IDS = [
    VITALITY_ID,
    37, 33, 34, 35, 36,
    87, 85,
    124, 121, 141, 142,
];

const CORE_SPELL_OFFENSE_IDS = [
    POWER_ID,
    DAMAGE_ID,
    CRIT_ID,
    CRIT_DAMAGE_ID,
    SPELL_PER_DMG_ID,
    FINAL_DAMAGE_ID,
];

function collectPrimaryLinesRecursive(row, out = [], depth = 0) {
    if (!row || depth > 6) return out;
    out.push(
        ...(row.baseLines || []),
        ...((row.conditionalBlocks || []).flatMap((b) => b.lines || [])),
    );
    (row.summonChildRows || []).forEach((child) => collectPrimaryLinesRecursive(child, out, depth + 1));
    return out;
}

function isEligiblePrimaryLine(line) {
    if (!line || !['damage', 'heal', 'push', 'best', 'best_heal'].includes(line.kind)) return false;
    if (Array.isArray(line.conditions) && line.conditions.length > 0) {
        const ids = new Set(
            line.conditions
                .map((c) => c?.id ?? c?.Id)
                .filter((id) => id != null)
                .map((id) => String(id))
        );
        const allowed = new Set(['through_portal', 'direct']);
        for (const id of ids) {
            if (!allowed.has(id)) return false;
        }
    }
    return true;
}

function addIdsFromLine(line, ids, flags) {
    if (!line || !isEligiblePrimaryLine(line)) return;
    if (line.kind === 'damage') {
        flags.anySpellDmg = true;
        if (line.el && ELM_STAT_ID[line.el]) ids.add(ELM_STAT_ID[line.el]);
        if (line.el && ELM_DMG_ID[line.el]) ids.add(ELM_DMG_ID[line.el]);
        return;
    }
    if (line.kind === 'best') {
        flags.anySpellDmg = true;
        BEST_ELEM_PAIRS.forEach(([pr, ed]) => {
            ids.add(pr);
            ids.add(ed);
        });
        return;
    }
    if (line.kind === 'push') {
        flags.hasPush = true;
        ids.add(PUSH_DMG_ID);
        return;
    }
    if (line.kind === 'heal') {
        if (isPercentMaxHpHealLine(line)) {
            ids.add(VITALITY_ID);
            return;
        }
        if (line.el && ELM_STAT_ID[line.el]) ids.add(ELM_STAT_ID[line.el]);
        ids.add(HEAL_STAT_ID);
        return;
    }
    if (line.kind === 'best_heal') {
        BEST_HEAL_PRIMARY_IDS.forEach((id) => ids.add(id));
        ids.add(HEAL_STAT_ID);
    }
}

function criterionSpellId(criterion) {
    return Number(
        typeof criterion === 'number'
            ? criterion
            : (criterion?.spell_id ?? criterion?.spellId ?? criterion?.id)
    );
}

function criterionLineKeys(criterion) {
    if (!criterion || typeof criterion !== 'object') return [];
    return (criterion.line_keys ?? criterion.lineKeys ?? [])
        .map((k) => String(k || '').trim())
        .filter(Boolean);
}

function lineMatchesCriterion(line, lineKeys) {
    if (!line || !Array.isArray(line?.sourceKeys) || !lineKeys.length) return false;
    const set = new Set(line.sourceKeys.map((x) => String(x || '').trim()).filter(Boolean));
    return lineKeys.some((k) => set.has(String(k || '').trim()));
}

/**
 * Charac ids that influence spell primary totals (aligned with backend infer_spell_stat_ids).
 */
export function inferSpellStatIds(build, spellData, lang = 'fr') {
    const criteria = typeof build?.getMaximizeSpellCriteria === 'function'
        ? build.getMaximizeSpellCriteria()
        : [];
    const normalized = Array.isArray(criteria) ? criteria : [];
    if (!normalized.length || !spellData) return new Set();

    const stats = build.getStats?.() || {};
    const lvl = Number(build?.lvl) || 200;
    const clsName = build?.getCharacterClass?.()?.name || build?.characterClass?.name;
    const eliotropePortalCardOnly = isEliotropeClass(clsName);
    const spells = [
        ...(Array.isArray(spellData.spells) ? spellData.spells : []),
        ...(Array.isArray(spellData.other) ? spellData.other : []),
    ];
    const rows = calcSpellRows({
        spells,
        lvl,
        stats,
        lang,
        eliotropePortalCardOnly,
        summonCatalog: spellData.summonCatalog || {},
    });
    const rowById = new Map(rows.map((r) => [Number(r.id), r]));

    const ids = new Set(CORE_SPELL_OFFENSE_IDS);
    const flags = { hasPush: false, anySpellDmg: false, anyWeaponDmg: false };

    normalized.forEach((criterion) => {
        const spellId = criterionSpellId(criterion);
        if (!Number.isFinite(spellId)) return;
        const row = rowById.get(spellId);
        if (!row) return;
        const lineKeys = criterionLineKeys(criterion);
        const lines = collectPrimaryLinesRecursive(row).filter(isEligiblePrimaryLine);
        let targetLines;
        if (lineKeys.length) {
            targetLines = lines.filter((line) => lineMatchesCriterion(line, lineKeys));
        } else {
            const firstDamage = lines.find((line) => (
                line.kind === 'damage' || line.kind === 'best'
            ));
            const firstPush = lines.find((line) => line.kind === 'push');
            const firstHeal = lines.find((line) => (
                line.kind === 'heal' || line.kind === 'best_heal'
            ));
            targetLines = [firstDamage, firstPush, firstHeal].filter(Boolean);
        }
        targetLines.forEach((line) => addIdsFromLine(line, ids, flags));
    });

    if (flags.anySpellDmg) {
        ids.add(SPELL_PER_DMG_ID);
        ids.add(FINAL_DAMAGE_ID);
    }
    if (flags.anyWeaponDmg) {
        ids.add(WEAPON_PER_DMG_ID);
        ids.add(FINAL_DAMAGE_ID);
    }
    if (flags.hasPush) ids.add(PUSH_DMG_ID);

    return ids;
}

export function statIdsFromStatsCriteria(statsCriteria) {
    const out = new Set();
    if (!statsCriteria || typeof statsCriteria !== 'object') return out;
    Object.keys(statsCriteria).forEach((rawId) => {
        const statId = Number(rawId);
        if (!Number.isFinite(statId)) return;
        const rule = statsCriteria[rawId];
        if (!rule || typeof rule !== 'object') return;
        if (Number.isFinite(Number(rule.min)) || rule.maximize === true) {
            out.add(statId);
        }
    });
    return out;
}

export function isDefenseModeOn(defenseShare) {
    return Number(defenseShare) > 0;
}

export function buildCardRelevantStatIds(build, draft, options = {}) {
    const { spellData = null, defenseShare = 0 } = options;
    const ordered = [];
    const seen = new Set();

    const push = (statId) => {
        const id = Number(statId);
        if (!Number.isFinite(id) || seen.has(id)) return;
        seen.add(id);
        ordered.push(id);
    };

    push(AP_ID);
    push(MP_ID);

    const spellIds = inferSpellStatIds(build, spellData, options.lang);
    const hasSpells = spellIds.size > 0;
    spellIds.forEach((id) => push(id));

    const statsCriteria = draft?.req?.stats_criteria || {};
    statIdsFromStatsCriteria(statsCriteria).forEach((id) => {
        if (hasSpells && ELEMENTAL_STAT_IDS.has(id)) return;
        push(id);
    });

    if (isDefenseModeOn(defenseShare)) {
        DEFENSE_CARD_STAT_IDS.forEach((id) => push(id));
    }

    return ordered;
}

export function spellTypeIdForBuild(build, draft) {
    const slug = build?.getCharacterClass?.()?.name
        || build?.characterClass?.name
        || draft?.draft_metadata?.characterClass;
    return getTypeId(slug);
}
